import { useState } from 'react';
import { IncrementalListFooter } from '../components/ui/IncrementalListFooter';
import { useIncrementalItems } from '../components/ui/useIncrementalItems';
import { STANDARD_LIST_BATCH_SIZE } from '../components/ui/list-batch-sizes';
import { useNavigate } from 'react-router-dom';

import { useAuth } from './AuthProvider';
import { hasUnavailableOrganizationAccess } from './organization-access';
import { activateRoute42Memberships } from './membership-client';
import type { Route42Membership } from './types';
import { queryClient } from '../app/query-client';

function unavailableMembership(membership: Route42Membership): { label: string; description: string } {
  if (membership.customer_status === 'SUSPENDED') {
    return { label: 'Suspended', description: 'Organization suspended — view access status' };
  }
  if (membership.membership_status === 'SUSPENDED') {
    return { label: 'Suspended', description: 'Your access is suspended — view access status' };
  }
  if (membership.customer_status === 'DEACTIVATED') {
    return { label: 'Deactivated', description: 'Organization deactivated — contact Route42 support' };
  }
  if (membership.customer_status === 'PROVISIONING') {
    return { label: 'Setting up', description: 'Organization setup in progress — check again later' };
  }
  if (membership.membership_status === 'INVITED') {
    return { label: 'Pending', description: 'Membership invitation pending — refresh organizations to activate access' };
  }
  return { label: 'Unavailable', description: 'Organization access unavailable — contact its administrator' };
}

export function CustomerSelectionPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  // Display every non-removed membership returned by the authenticated API.
  // Only access_available memberships may be selected; this is not an access grant.
  const memberships = (auth.profile?.memberships ?? [])
    .filter((membership) => membership.membership_status !== 'REMOVED')
    .sort((a, b) => Number(b.customer_id === auth.activeCustomerId) - Number(a.customer_id === auth.activeCustomerId));
  const accessible = memberships.filter((membership) => membership.access_available);
  const [search, setSearch] = useState('');
  const filtered = memberships.filter((membership) => (membership.customer_name ?? membership.customer_id).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const pager = useIncrementalItems(filtered, STANDARD_LIST_BATCH_SIZE, `orgs:${auth.profile?.user?.email ?? ''}:${search}`);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  async function refreshOrganizations() {
    setRefreshing(true);
    setRefreshError(null);
    try {
      // Activation is identity-scoped and server-authorized. It may claim a
      // newly issued invitation; it never grants an arbitrary tenant membership.
      await activateRoute42Memberships();
      await auth.refreshProfile();
    } catch {
      setRefreshError('Could not refresh organization access. Please try again.');
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section className="r42-page r42-form-page">
      <span className="r42-eyebrow">Organization</span>
      <h1>Select your Route42 organization</h1>
      <p className="text-secondary">Your machine and workspace context will be scoped to this organization. Showing memberships associated with {auth.profile?.user?.email ?? 'your signed-in account'}.</p>
      {accessible.length === 1 ? (
        <p className="r42-organization-selection-note" role="status">
          Only one organization currently has active access for this account. Other memberships, if present, are shown below with their status. If an expected organization is absent, verify that this same sign-in account was invited, then refresh.
        </p>
      ) : null}
      {!accessible.length ? (
        <p className="r42-organization-selection-note" role="status">No active organization is available to this account.</p>
      ) : null}
      <label className="r42-search mt-3">
        <span className="visually-hidden">Search organizations</span>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search organizations…" />
      </label>
      <div className="r42-list-panel mt-4" aria-label="Your Route42 organizations">
        {pager.visible.map((membership) => {
          const unavailable = !membership.access_available;
          const canOpenStatus = unavailable && hasUnavailableOrganizationAccess(membership);
          const status = unavailable ? unavailableMembership(membership) : null;
          const content = (
            <>
              <span>
                <strong>{membership.customer_name ?? membership.customer_id}</strong>
                {membership.customer_id === auth.activeCustomerId && membership.access_available ? <small>Current organization</small> : null}
                <small>{status ? status.description : (membership.role_ids.join(', ') || 'Route42 member')}</small>
              </span>
              <span className="r42-customer-row-state">
                {status ? <span className="r42-customer-status">{status.label}</span> : null}
                {!unavailable || canOpenStatus ? <i className="bi bi-chevron-right" aria-hidden="true" /> : null}
              </span>
            </>
          );
          // Pending, provisioning and deactivated memberships are informational,
          // not clickable: never route them into a workspace.
          if (unavailable && !canOpenStatus) {
            return <div className="r42-customer-row is-unavailable r42-customer-row--informational" key={membership.membership_id}>{content}</div>;
          }
          return (
            <button
              type="button"
              className={`r42-customer-row${unavailable ? ' is-unavailable' : ''}`}
              key={membership.membership_id}
              onClick={() => {
                if (canOpenStatus) {
                  navigate(`/organization-unavailable?organization=${encodeURIComponent(membership.customer_id)}`);
                  return;
                }
                // Clear cached tenant data before opening another organization's workspace.
                if (membership.customer_id !== auth.activeCustomerId) queryClient.clear();
                auth.selectCustomer(membership.customer_id);
                navigate('/start', { replace: true });
              }}
            >
              {content}
            </button>
          );
        })}
      </div>
      {!pager.total && memberships.length ? <p className="r42-inline-state">No matching organizations.</p> : null}
      <IncrementalListFooter label="organizations" loaded={pager.loaded} total={pager.total} hasMore={pager.hasMore} onLoadMore={pager.loadMore} />
      <div className="r42-organization-selection-actions">
        <button type="button" className="btn btn-outline-secondary" disabled={refreshing} onClick={() => void refreshOrganizations()}>
          <i className="bi bi-arrow-clockwise me-2" aria-hidden="true" />{refreshing ? 'Refreshing…' : 'Refresh organizations'}
        </button>
        {refreshError ? <p role="alert">{refreshError}</p> : null}
      </div>
    </section>
  );
}
